import { FC, ReactNode, useMemo } from 'react';
import { IonContent } from '@ionic/react';
import OrderItem from '../tabs/orders/components/Orders/OrderItem';
import OrderPaymentBreakdown from '../../../../components-library/order/OrderPaymentBreakdown';
import { Order } from '../../../../services/exports/Interfaces';
import toFlatArray from '../../../../utils/to-flat.util';
import { CompleteBasketItem } from '../../../../providers/BasketProvider';
import PageHeader from '../../components/headers/PageHeader';
import OrderBasketItem from '../../../../components-library/basket/order-basket-item';
import { useTranslation } from 'react-i18next';
import Button from '../../components/buttons/Button';
import useBasket from '../../../../hooks/basket/useBasket';
import OrderRating from '../../../../components-library/order/OrderRating';
import { Routes } from '../../exports/Enums';
import useRouter from '../../../../hooks/navigation/useRouter';
import { ORDER_FULFILLMENT_STATUSES } from '../../../../services/exports/Constants';
import { useGetOrderQuery } from '../../../../store/api-slices';

type Props =
  | { uuid: string }
  | {
      order: Order;
    };

const OrderDetailsPage: FC<Props> = (props): ReactNode => {
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Pages:OrderDetailsPage' });
  const { addItems } = useBasket();
  const { navigate, reset } = useRouter();
  const skipFetchOrder = !('uuid' in props);
  const { data: fetchedOrder } = useGetOrderQuery(!skipFetchOrder && props.uuid, {
    skip: skipFetchOrder,
    refetchOnMountOrArgChange: 30,
  });
  const data = skipFetchOrder ? props.order : fetchedOrder?.data;
  const completeBasketItems = useMemo(() => {
    return (
      data &&
      data.line_items.map(
        (item) =>
          ({
            ...item,
            entry: item,
            modifiers: toFlatArray(item.modifiers, 'modifiers').map((entry) => ({ ...entry, entry })),
          }) as unknown as CompleteBasketItem,
      )
    );
  }, [data?.line_items]);
  const hasOrderRating = !data?.rates?.length;
  const withEarnedPoints = data?.fulfillment?.status !== ORDER_FULFILLMENT_STATUSES.REJECTED;
  const onOrderAgainClick = (): void => {
    addItems(completeBasketItems);
    navigate({
      mobile: {
        path: Routes.Cart,
      },
    });
  };

  const onBackClick = (): void => {
    reset();
  };

  return (
    <>
      <PageHeader title={t('title')} onBackClick={onBackClick} />
      {data && (
        <IonContent className="[&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-gray-200 [&::part(scroll)]:py-lg [&::part(scroll)]:px-md">
          <OrderItem data={data} className="mb-none" />
          <div className="space-y-md py-md">
            {completeBasketItems.map((item, index) => (
              <OrderBasketItem key={index} data={item} coupon_type={data?.coupon_type} index={index} withImage />
            ))}
          </div>
          <OrderPaymentBreakdown
            order={data}
            className="font-gray-700 font-semibold py-md !border-b-none"
            withSeparatedTotal
            withPaymentMethod
            withEarnedPoints={withEarnedPoints}
          />
          {hasOrderRating && (
            <OrderRating order={data} classes={{ container: '!border-b-none text-center', wrapper: 'mx-auto' }} />
          )}
          <Button title={t('order_again')} onClick={onOrderAgainClick} className="mt-md" />
        </IonContent>
      )}
    </>
  );
};

export default OrderDetailsPage;
